import { FieldProps } from 'formik'
import React from 'react'
import { Element } from 'react-bulma-components'
import FormField, { FormFieldProps } from './form-field'

interface RadioOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

interface RadioFieldProps extends FormFieldProps, FieldProps {
  /** Options pour les boutons radio */
  options: RadioOption[]
  /** Nom du groupe radio (doit être unique) */
  name: string
  /** Valeur actuellement sélectionnée */
  value?: string
  /** Fonction de changement */
  onChange?: (value: string) => void
  /** Désactivé */
  disabled?: boolean
}

/**
 * Composant pour gérer un groupe de boutons radio
 * S'intègre avec Formik comme les autres champs
 */
const RadioField: React.FunctionComponent<RadioFieldProps> = ({
  field,
  form,
  label,
  help,
  options,
  name,
  value,
  onChange,
  disabled = false,
  ...rest
}) => {
  // Utilise la valeur de Formik si disponible, sinon la prop value
  const currentValue = field?.value || value

  const handleChange = (optionValue: string) => {
    if (field) {
      form.setFieldValue(field.name, optionValue)
    }
    if (onChange) {
      onChange(optionValue)
    }
  }

  return (
    <FormField
      label={label}
      help={help}
      error={form?.errors[field?.name] as string}
      required={rest.required}
      disabled={disabled}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {options.map(option => (
          <label
            key={option.value}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={currentValue === option.value}
              onChange={() => handleChange(option.value)}
              disabled={disabled || option.disabled}
              style={{ marginTop: '0.25rem' }}
              data-test={`radio-${option.value}`}
            />
            <div>
              <Element renderAs="span" textWeight="semibold">
                {option.label}
              </Element>
              {option.description && (
                <Element renderAs="div" textSize="small" textColor="grey">
                  {option.description}
                </Element>
              )}
            </div>
          </label>
        ))}
      </div>
    </FormField>
  )
}

export default RadioField
